import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, FlatList, Alert, Text, ScrollView, Platform, Pressable } from 'react-native';
import {
  FAB, ActivityIndicator, Portal, Modal, Button, Checkbox,
} from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchBoitiers, createBoitier, updateBoitierStatut, deleteBoitier,
} from '../redux/slices/stockBoitierSlice';
import {
  clearSimRechargeJob, createSim, createSimRechargeJob, deleteSim,
  fetchSimRechargeJob, fetchSims, isRechargeJobTerminal, stopSimRechargeJob, updateSimStatut,
} from '../redux/slices/stockCarteSimSlice';
import { fetchModelesBoitier } from '../redux/slices/modeleBoitierSlice';
import { CONTENT_MAX_WIDTH, RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import {
  Badge, FilterChip, IconCircleButton, PageHeader, SearchPill, SectionHeader, TextInput,
} from '../components/ui';
import type { BadgeTone } from '../components/ui';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import { StatutStock, StockBoitier, StockCarteSim } from '../types';
import stockCarteSimService from '../services/stockCarteSimService';
import SimRechargeProgressModal from '../components/SimRechargeProgressModal';

type ActiveTab = 'BOITIER' | 'SIM';
type EditedItem = { id: number; type: ActiveTab; statut: StatutStock; ref: string; modele?: string };
type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

const STATUT_TONES: Record<StatutStock, BadgeTone> = {
  DISPONIBLE: 'success',
  AFFECTE: 'warning',
  DEFECTUEUX: 'danger',
};
const STATUT_LABELS: Record<StatutStock, string> = {
  DISPONIBLE: 'Disponible',
  AFFECTE: 'Affecté',
  DEFECTUEUX: 'Défectueux',
};
const STATUT_ICONS: Record<StatutStock, IconName> = {
  DISPONIBLE: 'check-circle-outline',
  AFFECTE: 'link-variant',
  DEFECTUEUX: 'alert-circle-outline',
};
const TAB_ICONS: Record<ActiveTab, IconName> = {
  BOITIER: 'router-wireless',
  SIM: 'sim-outline',
};

const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`;

const isValidImei = (v: string) => /^\d{15}$/.test(v);
const isValidSim = (v: string) => /^\d{10}$/.test(v);
const formatDateAffectation = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
};
const RECHARGE_SEUIL_MOIS = 2;
const RECHARGE_POLL_INTERVAL_MS = 3000;
const RECHARGE_MAX_POLLS_WITHOUT_PROGRESS = 20;
const isRechargeExpiree = (dateDerniereRecharge?: string) => {
  if (!dateDerniereRecharge) return true;
  const d = new Date(dateDerniereRecharge);
  if (Number.isNaN(d.getTime())) return true;
  const seuil = new Date();
  seuil.setMonth(seuil.getMonth() - RECHARGE_SEUIL_MOIS);
  return d < seuil;
};

/** Airbnb-style category tabs: icon above label, ink underline on the active tab. */
function CategoryTabs({
  value,
  onChange,
  tabs,
}: {
  value: ActiveTab;
  onChange: (value: ActiveTab) => void;
  tabs: { value: ActiveTab; label: string }[];
}) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <View style={styles.tabs} accessibilityRole="tablist">
      {tabs.map((tab) => {
        const active = tab.value === value;
        return (
          <Pressable
            key={tab.value}
            onPress={() => onChange(tab.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            style={styles.tab}
          >
            {(state) => {
              const hovered = (state as { hovered?: boolean }).hovered;
              return (
                <>
                  <MaterialCommunityIcons
                    name={TAB_ICONS[tab.value]}
                    size={24}
                    color={active || hovered ? colors.textPrimary : colors.textSecondary}
                  />
                  <Text style={[styles.tabLabel, (active || hovered) && styles.tabLabelActive]} numberOfLines={1}>
                    {tab.label}
                  </Text>
                  <View
                    style={[
                      styles.tabUnderline,
                      active ? styles.tabUnderlineActive : hovered ? styles.tabUnderlineHovered : null,
                    ]}
                  />
                </>
              );
            }}
          </Pressable>
        );
      })}
    </View>
  );
}

/** Status badge that opens the edit modal (a chevron hints that it can be changed). */
function StatusPill({
  statut,
  onPress,
  accessibilityLabel,
}: {
  statut: StatutStock;
  onPress: () => void;
  accessibilityLabel: string;
}) {
  const styles = useStyles();
  const { colors } = useAppTheme();

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      hitSlop={6}
      style={(state) => [
        styles.statusPill,
        ((state as { hovered?: boolean }).hovered || state.pressed) && styles.statusPillHovered,
      ]}
    >
      <Badge label={STATUT_LABELS[statut]} tone={STATUT_TONES[statut]} dot />
      <MaterialCommunityIcons name="chevron-down" size={16} color={colors.textSecondary} />
    </Pressable>
  );
}

export default function GestionStock() {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, gutter, width: windowWidth } = useLayout();
  const modalWidth = Math.min(560, windowWidth - 32);
  const dispatch = useAppDispatch();
  const { list: boitiers, loading: loadingB } = useAppSelector((s) => s.stockBoitier);
  const {
    list: sims,
    loading: loadingS,
    activeRechargeJob,
    rechargeJobCreating,
    rechargeJobPolling,
    rechargeJobError,
  } = useAppSelector((s) => s.stockSim);
  const { list: modeles } = useAppSelector((s) => s.modelesBoitier);
  const finalizedRechargeJobIds = useRef<Set<number>>(new Set());
  const rechargeSubmissionInFlight = useRef(false);
  const rechargePollsWithoutProgress = useRef(0);
  const lastRechargeProgress = useRef<string | null>(null);

  const [activeTab, setActiveTab] = useState<ActiveTab>('BOITIER');

  const [showAddBoitierModal, setShowAddBoitierModal] = useState(false);
  const [addImeis, setAddImeis] = useState<string[]>(['']);
  const [addBoitierModele, setAddBoitierModele] = useState('');
  const [savingBoitier, setSavingBoitier] = useState(false);

  const [showAddSimModal, setShowAddSimModal] = useState(false);
  const [addNumeros, setAddNumeros] = useState<string[]>(['']);
  const [savingSim, setSavingSim] = useState(false);

  const [simSelectionMode, setSimSelectionMode] = useState(false);
  const [selectedSimIds, setSelectedSimIds] = useState<number[]>([]);
  const [showRechargeProgress, setShowRechargeProgress] = useState(false);
  const [rechargePollingPaused, setRechargePollingPaused] = useState(false);
  const [rechargeTrackingMessage, setRechargeTrackingMessage] = useState<string | null>(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<EditedItem | null>(null);
  const [editStatut, setEditStatut] = useState<StatutStock>('DISPONIBLE');
  const [editRef, setEditRef] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Filtres
  const [filterImei, setFilterImei] = useState('');
  const [filterNumero, setFilterNumero] = useState('');
  const [filterModele, setFilterModele] = useState('');
  const [filterStatutBoitier, setFilterStatutBoitier] = useState<StatutStock | null>(null);
  const [filterStatutSim, setFilterStatutSim] = useState<StatutStock | null>(null);
  const [filterImeiDebounced, setFilterImeiDebounced] = useState('');
  const [filterNumeroDebounced, setFilterNumeroDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setFilterImeiDebounced(filterImei), 300);
    return () => clearTimeout(t);
  }, [filterImei]);

  useEffect(() => {
    const t = setTimeout(() => setFilterNumeroDebounced(filterNumero), 300);
    return () => clearTimeout(t);
  }, [filterNumero]);

  const filteredBoitiers = boitiers.filter((b) => {
    const matchImei = !filterImeiDebounced || b.imei.includes(filterImeiDebounced);
    const matchModele = !filterModele || (b.modele ?? '').toLowerCase() === filterModele.toLowerCase();
    const matchStatut = !filterStatutBoitier || b.statut === filterStatutBoitier;
    return matchImei && matchModele && matchStatut;
  });

  const filteredSims = sims.filter((s) => {
    const matchNumero = !filterNumeroDebounced || s.numero.includes(filterNumeroDebounced);
    const matchStatut = !filterStatutSim || s.statut === filterStatutSim;
    return matchNumero && matchStatut;
  });

  const numerosRechargeExpiree = sims
    .filter((s) => isRechargeExpiree(s.dateDerniereRecharge))
    .map((s) => s.numero);

  const load = useCallback(() => {
    dispatch(fetchBoitiers());
    dispatch(fetchSims());
    dispatch(fetchModelesBoitier());
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  const rechargeJobRunning = Boolean(
    activeRechargeJob && !isRechargeJobTerminal(activeRechargeJob.status)
  );
  const rechargingSim = rechargeJobCreating || rechargeJobRunning;

  useEffect(() => {
    rechargePollsWithoutProgress.current = 0;
    lastRechargeProgress.current = activeRechargeJob?.updatedAt ?? null;
    setRechargePollingPaused(false);
    setRechargeTrackingMessage(null);
  }, [activeRechargeJob?.id]);

  useEffect(() => {
    if (
      !showRechargeProgress ||
      !activeRechargeJob ||
      rechargePollingPaused ||
      isRechargeJobTerminal(activeRechargeJob.status)
    ) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const job = await dispatch(fetchSimRechargeJob(activeRechargeJob.id)).unwrap();
        const progressKey = [
          job.updatedAt,
          job.status,
          job.pendingCount,
          job.inProgressCount,
          job.succeededCount,
          job.failedCount,
        ].join(':');

        if (progressKey === lastRechargeProgress.current) {
          rechargePollsWithoutProgress.current += 1;
        } else {
          lastRechargeProgress.current = progressKey;
          rechargePollsWithoutProgress.current = 0;
        }

        if (isRechargeJobTerminal(job.status)) {
          return;
        }
      } catch {
        rechargePollsWithoutProgress.current += 1;
      }

      if (
        rechargePollsWithoutProgress.current >= RECHARGE_MAX_POLLS_WITHOUT_PROGRESS
      ) {
        setRechargePollingPaused(true);
        setRechargeTrackingMessage(
          "Aucune progression détectée. Vérifiez que l'agent Windows est démarré, puis reprenez le suivi.",
        );
        return;
      }

      if (!cancelled) {
        timer = setTimeout(poll, RECHARGE_POLL_INTERVAL_MS);
      }
    };

    timer = setTimeout(poll, RECHARGE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    activeRechargeJob?.id,
    activeRechargeJob?.status,
    dispatch,
    rechargePollingPaused,
    showRechargeProgress,
  ]);

  useEffect(() => {
    if (
      !activeRechargeJob ||
      !isRechargeJobTerminal(activeRechargeJob.status) ||
      finalizedRechargeJobIds.current.has(activeRechargeJob.id)
    ) {
      return;
    }

    finalizedRechargeJobIds.current.add(activeRechargeJob.id);
    dispatch(fetchSims());

    const failedIds = activeRechargeJob.items
      .filter((item) => item.status === 'FAILED')
      .map((item) => item.simId);
    setSelectedSimIds(failedIds);
    setSimSelectionMode(failedIds.length > 0);
  }, [activeRechargeJob, dispatch]);

  const cntBDispo = boitiers.filter((i) => i.statut === 'DISPONIBLE').length;
  const cntBAffecte = boitiers.filter((i) => i.statut === 'AFFECTE').length;
  const cntBDef = boitiers.filter((i) => i.statut === 'DEFECTUEUX').length;
  const cntSDispo = sims.filter((i) => i.statut === 'DISPONIBLE').length;
  const cntSAffecte = sims.filter((i) => i.statut === 'AFFECTE').length;
  const cntSDef = sims.filter((i) => i.statut === 'DEFECTUEUX').length;
  const activeStatutFilter = activeTab === 'BOITIER' ? filterStatutBoitier : filterStatutSim;

  const toggleStatutFilter = (statut: StatutStock) => {
    if (activeTab === 'BOITIER') {
      setFilterStatutBoitier((current) => (current === statut ? null : statut));
      return;
    }
    setFilterStatutSim((current) => (current === statut ? null : statut));
  };

  const openAddBoitierModal = () => { setAddImeis(['']); setAddBoitierModele(''); setShowAddBoitierModal(true); };
  const addImeiLine = () => setAddImeis((p) => [...p, '']);
  const removeImeiLine = (idx: number) => setAddImeis((p) => p.filter((_, i) => i !== idx));
  const updateImeiLine = (idx: number, val: string) =>
    setAddImeis((p) => p.map((v, i) => (i === idx ? val.replace(/\D/g, '').slice(0, 15) : v)));

  const handleAddBoitiers = async () => {
    const modele = addBoitierModele.trim();
    const valid = addImeis.filter(isValidImei);
    if (valid.length === 0) { Alert.alert('Erreur', 'Veuillez saisir au moins un IMEI valide (15 chiffres).'); return; }
    if (!modele) { Alert.alert('Erreur', 'Le modele du boitier est obligatoire.'); return; }
    setSavingBoitier(true);
    try {
      await Promise.all(valid.map((imei) => dispatch(createBoitier({ imei, modele })).unwrap()));
      setShowAddBoitierModal(false);
    } catch (e: any) {
      Alert.alert('Erreur', e?.message ?? "Impossible d'ajouter le(s) boitier(s).");
    } finally { setSavingBoitier(false); }
  };

  const openAddSimModal = () => { setAddNumeros(['']); setShowAddSimModal(true); };
  const addSimLine = () => setAddNumeros((p) => [...p, '']);
  const removeSimLine = (idx: number) => setAddNumeros((p) => p.filter((_, i) => i !== idx));
  const updateSimLine = (idx: number, val: string) =>
    setAddNumeros((p) => p.map((v, i) => (i === idx ? val.replace(/\D/g, '').slice(0, 10) : v)));

  const handleAddSims = async () => {
    const valid = addNumeros.filter(isValidSim);
    if (valid.length === 0) { Alert.alert('Erreur', 'Veuillez saisir au moins un numero valide (10 chiffres).'); return; }
    setSavingSim(true);
    try {
      await Promise.all(valid.map((numero) => dispatch(createSim({ numero })).unwrap()));
      setShowAddSimModal(false);
    } catch (e: any) {
      Alert.alert('Erreur', e?.message ?? "Impossible d'ajouter la(les) carte(s) SIM.");
    } finally { setSavingSim(false); }
  };

  const toggleSimSelectionMode = () => {
    setSimSelectionMode((p) => !p);
    setSelectedSimIds([]);
  };

  const toggleSimSelected = (id: number) => {
    setSelectedSimIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const handleRechargeSims = async (ids: number[]) => {
    if (ids.length === 0) return;
    if (rechargingSim || rechargeSubmissionInFlight.current) {
      setShowRechargeProgress(true);
      return;
    }

    rechargeSubmissionInFlight.current = true;
    rechargePollsWithoutProgress.current = 0;
    lastRechargeProgress.current = null;
    setRechargePollingPaused(false);
    setRechargeTrackingMessage(null);
    setShowRechargeProgress(true);
    try {
      await dispatch(createSimRechargeJob({ simIds: ids })).unwrap();
    } catch (e: unknown) {
      const msg = typeof e === 'string'
        ? e
        : e instanceof Error
          ? e.message
          : "Impossible de mettre la recharge en file d'attente.";
      Alert.alert('Erreur', msg);
    } finally {
      rechargeSubmissionInFlight.current = false;
    }
  };

  const closeRechargeProgress = () => {
    if ((rechargeJobRunning && !rechargePollingPaused) || rechargeJobCreating) return;
    setShowRechargeProgress(false);
    if (!rechargeJobRunning) {
      dispatch(clearSimRechargeJob());
    }
  };

  const handleStopRecharge = () => {
    if (!activeRechargeJob || isRechargeJobTerminal(activeRechargeJob.status)) return;
    const message = 'Arrêter cette recharge ? Les cartes encore en file ou en cours seront marquées en échec.';
    const run = async () => {
      try {
        await dispatch(stopSimRechargeJob(activeRechargeJob.id)).unwrap();
        setRechargePollingPaused(false);
        setRechargeTrackingMessage(null);
      } catch (e: unknown) {
        const msg = typeof e === 'string'
          ? e
          : e instanceof Error
            ? e.message
            : 'Impossible d\'arrêter la recharge.';
        Alert.alert('Erreur', msg);
      }
    };

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) void run();
      return;
    }

    Alert.alert('Arrêter la recharge', message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Arrêter', style: 'destructive', onPress: () => void run() },
    ]);
  };

  const resumeRechargeTracking = () => {
    rechargePollsWithoutProgress.current = 0;
    lastRechargeProgress.current = activeRechargeJob?.updatedAt ?? null;
    setRechargeTrackingMessage(null);
    setRechargePollingPaused(false);
    setShowRechargeProgress(true);
  };

  const handleDownloadNumerosExpirees = async () => {
    if (numerosRechargeExpiree.length === 0) {
      Alert.alert('Aucun numéro', 'Aucune carte SIM n\'a une recharge de plus de 2 mois.');
      return;
    }
    try {
      await stockCarteSimService.downloadNumeros(numerosRechargeExpiree);
    } catch (e: any) {
      Alert.alert('Erreur', e?.message ?? 'Impossible de télécharger les numéros.');
    }
  };

  const confirmRechargeSims = (ids: number[], ref?: string) => {
    const message = ids.length > 1
      ? `Confirmer la recharge de ${ids.length} cartes SIM selectionnees ?`
      : `Confirmer la recharge de la carte SIM "${ref}" ?`;

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) void handleRechargeSims(ids);
      return;
    }

    Alert.alert('Confirmer', message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Recharger', onPress: () => void handleRechargeSims(ids) },
    ]);
  };

  const handleEditOpen = (item: StockBoitier | StockCarteSim, type: ActiveTab) => {
    const ref = type === 'BOITIER' ? (item as StockBoitier).imei : (item as StockCarteSim).numero;
    setEditingItem({ id: item.id, type, statut: item.statut, ref, modele: item.modele });
    setEditStatut(item.statut);
    setEditRef(ref);
    setShowEditModal(true);
  };

  const handleUpdateStatut = async () => {
    if (!editingItem) return;
    setSavingEdit(true);
    try {
      const refChanged = editRef !== editingItem.ref;
      if (editingItem.type === 'BOITIER') {
        await dispatch(updateBoitierStatut({
          id: editingItem.id,
          statut: editStatut,
          ...(refChanged ? { imei: editRef } : {}),
        })).unwrap();
      } else {
        await dispatch(updateSimStatut({
          id: editingItem.id,
          statut: editStatut,
          ...(refChanged ? { numero: editRef } : {}),
        })).unwrap();
      }
      setShowEditModal(false);
    } catch (e: any) {
      Alert.alert('Erreur', e?.message ?? 'Impossible de modifier cet article.');
    }
    finally { setSavingEdit(false); }
  };

  const executeDelete = async (
    id: number,
    type: ActiveTab,
    onSuccess?: () => void,
  ) => {
    try {
      if (type === 'BOITIER') {
        await dispatch(deleteBoitier(id)).unwrap();
      } else {
        await dispatch(deleteSim(id)).unwrap();
      }
      if (onSuccess) onSuccess();
    } catch (e: any) {
      const msg = e?.response?.data?.erreur
        ?? e?.response?.data?.message
        ?? e?.message
        ?? "Impossible de supprimer l'article.";
      Alert.alert('Erreur', msg);
    }
  };

  const confirmDeleteAction = (ref: string, onConfirm: () => void) => {
    const message = `Supprimer "${ref}" du stock ?`;

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) onConfirm();
      return;
    }

    Alert.alert('Confirmer', message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Supprimer', style: 'destructive', onPress: onConfirm },
    ]);
  };

  const handleDelete = () => {
    if (!editingItem) return;
    if (editingItem.statut === 'AFFECTE') {
      Alert.alert('Suppression impossible', 'Seuls les elements non affectes peuvent etre supprimes.');
      return;
    }

    confirmDeleteAction(editingItem.ref, async () => {
      setDeleting(true);
      await executeDelete(editingItem.id, editingItem.type, () => setShowEditModal(false));
      setDeleting(false);
    });
  };

  const handleDeleteDirect = (item: StockBoitier | StockCarteSim, type: ActiveTab) => {
    const ref = type === 'BOITIER' ? (item as StockBoitier).imei : (item as StockCarteSim).numero;

    confirmDeleteAction(ref, () => {
      void executeDelete(item.id, type);
    });
  };

  const isBoitierTab = activeTab === 'BOITIER';
  const loadingList = loadingB || loadingS;
  const statutKpis: { statut: StatutStock; count: number }[] = isBoitierTab
    ? [
        { statut: 'DISPONIBLE', count: cntBDispo },
        { statut: 'AFFECTE', count: cntBAffecte },
        { statut: 'DEFECTUEUX', count: cntBDef },
      ]
    : [
        { statut: 'DISPONIBLE', count: cntSDispo },
        { statut: 'AFFECTE', count: cntSAffecte },
        { statut: 'DEFECTUEUX', count: cntSDef },
      ];
  const filtersActive = isBoitierTab
    ? Boolean(filterStatutBoitier || filterImeiDebounced || filterModele)
    : Boolean(filterStatutSim || filterNumeroDebounced);
  const totalCount = isBoitierTab ? boitiers.length : sims.length;
  const shownCount = isBoitierTab ? filteredBoitiers.length : filteredSims.length;

  const rowContainerStyle = (index: number, count: number, selected = false) => [
    styles.row,
    index === 0 && styles.rowFirst,
    index === count - 1 && styles.rowLast,
    selected && styles.rowSelected,
  ];
  const rowInnerStyle = (index: number) => [
    styles.rowInner,
    !isDesktop && styles.rowInnerMobile,
    index > 0 && styles.rowDivider,
  ];

  const renderBoitierItem = ({ item, index }: { item: StockBoitier; index: number }) => (
    <View style={rowContainerStyle(index, filteredBoitiers.length)}>
      <View style={rowInnerStyle(index)}>
        {isDesktop ? (
          <View style={styles.rowIcon}>
            <MaterialCommunityIcons name={TAB_ICONS.BOITIER} size={22} color={colors.textPrimary} />
          </View>
        ) : null}
        <View style={styles.rowMain}>
          <Text style={styles.itemRef} numberOfLines={1}>{item.imei}</Text>
          {item.modele ? <Text style={styles.itemModele} numberOfLines={1}>{item.modele}</Text> : null}
          {item.statut === 'AFFECTE' && item.dateAffectation ? (
            <Text style={styles.itemMeta}>Affecté le {formatDateAffectation(item.dateAffectation)}</Text>
          ) : null}
          {item.boitierClient ? (
            <View style={styles.itemBadges}>
              <Badge label="Boîtier client" tone="brand" icon="account" />
            </View>
          ) : null}
        </View>
        <View style={[styles.rowSide, isDesktop && styles.rowSideDesktop]}>
          <StatusPill
            statut={item.statut}
            onPress={() => handleEditOpen(item, 'BOITIER')}
            accessibilityLabel={`Modifier le boîtier ${item.imei}`}
          />
          {item.statut !== 'AFFECTE' && (
            <View style={styles.rowActions}>
              <IconCircleButton
                icon="delete-outline"
                variant="ghost"
                size={36}
                color={colors.danger}
                accessibilityLabel={`Supprimer le boîtier ${item.imei}`}
                onPress={() => handleDeleteDirect(item, 'BOITIER')}
              />
            </View>
          )}
        </View>
      </View>
    </View>
  );

  const renderSimItem = ({ item, index }: { item: StockCarteSim; index: number }) => {
    const rechargeExpiree = isRechargeExpiree(item.dateDerniereRecharge);
    const selected = selectedSimIds.includes(item.id);
    return (
      <View style={rowContainerStyle(index, filteredSims.length, simSelectionMode && selected)}>
        <View style={rowInnerStyle(index)}>
          {simSelectionMode && (
            <Checkbox
              status={selected ? 'checked' : 'unchecked'}
              onPress={() => toggleSimSelected(item.id)}
              color={colors.primary}
              uncheckedColor={colors.textSecondary}
            />
          )}
          {isDesktop ? (
            <View style={styles.rowIcon}>
              <MaterialCommunityIcons name={TAB_ICONS.SIM} size={22} color={colors.textPrimary} />
            </View>
          ) : null}
          <View style={styles.rowMain}>
            <Text style={styles.itemRef} numberOfLines={1}>{item.numero}</Text>
            {item.modele ? <Text style={styles.itemModele} numberOfLines={1}>{item.modele}</Text> : null}
            {item.statut === 'AFFECTE' && item.dateAffectation ? (
              <Text style={styles.itemMeta}>Affecté le {formatDateAffectation(item.dateAffectation)}</Text>
            ) : null}
            {item.dateDerniereRecharge ? (
              <Text style={styles.itemMeta}>Dernière recharge : {formatDateAffectation(item.dateDerniereRecharge)}</Text>
            ) : (
              <Text style={styles.itemMeta}>Aucune recharge enregistrée</Text>
            )}
            {rechargeExpiree && (
              <View style={styles.itemBadges}>
                <Badge label="Recharge de plus de 2 mois" tone="danger" icon="alert-circle-outline" />
              </View>
            )}
          </View>
          <View style={[styles.rowSide, isDesktop && styles.rowSideDesktop]}>
            <StatusPill
              statut={item.statut}
              onPress={() => handleEditOpen(item, 'SIM')}
              accessibilityLabel={`Modifier la carte SIM ${item.numero}`}
            />
            <View style={styles.rowActions}>
              <IconCircleButton
                icon="cash-plus"
                variant="ghost"
                size={36}
                color={colors.brand}
                accessibilityLabel={`Recharger la carte SIM ${item.numero}`}
                disabled={item.statut === 'DEFECTUEUX' || rechargingSim}
                onPress={() => confirmRechargeSims([item.id], item.numero)}
              />
              {item.statut !== 'AFFECTE' && (
                <IconCircleButton
                  icon="delete-outline"
                  variant="ghost"
                  size={36}
                  color={colors.danger}
                  accessibilityLabel={`Supprimer la carte SIM ${item.numero}`}
                  onPress={() => handleDeleteDirect(item, 'SIM')}
                />
              )}
            </View>
          </View>
        </View>
      </View>
    );
  };

  const listHeader = (
    <View>
      <PageHeader
        title="Stock"
        subtitle="Boîtiers GPS et cartes SIM : disponibilités, affectations et recharges."
        actions={isDesktop ? (
          <>
            <Button
              mode={isBoitierTab ? 'contained' : 'outlined'}
              icon={TAB_ICONS.BOITIER}
              textColor={isBoitierTab ? undefined : colors.textPrimary}
              onPress={openAddBoitierModal}
            >
              Ajouter des boîtiers
            </Button>
            <Button
              mode={isBoitierTab ? 'outlined' : 'contained'}
              icon={TAB_ICONS.SIM}
              textColor={isBoitierTab ? colors.textPrimary : undefined}
              onPress={openAddSimModal}
            >
              Ajouter des cartes SIM
            </Button>
          </>
        ) : undefined}
      />

      <CategoryTabs
        value={activeTab}
        onChange={setActiveTab}
        tabs={[
          { value: 'BOITIER', label: 'Boîtiers GPS' },
          { value: 'SIM', label: 'Cartes SIM' },
        ]}
      />

      {isDesktop ? (
        <View style={styles.kpiRow}>
          {statutKpis.map((kpi) => {
            const selected = activeStatutFilter === kpi.statut;
            return (
              <Pressable
                key={kpi.statut}
                onPress={() => toggleStatutFilter(kpi.statut)}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={`Filtrer par statut ${STATUT_LABELS[kpi.statut]}`}
                style={styles.kpiItem}
              >
                {(state) => {
                  const hovered = (state as { hovered?: boolean }).hovered;
                  return (
                    <StatCard
                      label={STATUT_LABELS[kpi.statut]}
                      value={String(kpi.count)}
                      tone={STATUT_TONES[kpi.statut]}
                      icon={STATUT_ICONS[kpi.statut]}
                      hint={selected ? 'Filtre actif · cliquer pour retirer' : 'Cliquer pour filtrer'}
                      style={[
                        styles.kpiCard,
                        hovered && !selected && styles.kpiCardHovered,
                        selected && styles.kpiCardSelected,
                        state.pressed && styles.kpiCardPressed,
                      ]}
                    />
                  );
                }}
              </Pressable>
            );
          })}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipScroll}
          contentContainerStyle={styles.chipRow}
        >
          {statutKpis.map((kpi) => (
            <FilterChip
              key={kpi.statut}
              label={STATUT_LABELS[kpi.statut]}
              count={kpi.count}
              icon={STATUT_ICONS[kpi.statut]}
              selected={activeStatutFilter === kpi.statut}
              onPress={() => toggleStatutFilter(kpi.statut)}
            />
          ))}
        </ScrollView>
      )}

      <SearchPill
        value={isBoitierTab ? filterImei : filterNumero}
        onChangeText={isBoitierTab ? setFilterImei : setFilterNumero}
        placeholder={isBoitierTab ? 'Rechercher un IMEI' : 'Rechercher un N° de téléphone'}
        style={[styles.search, isDesktop && styles.searchDesktop]}
      />

      {isBoitierTab ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipScroll}
          contentContainerStyle={styles.chipRow}
        >
          <FilterChip
            label="Tous les modèles"
            selected={!filterModele}
            onPress={() => setFilterModele('')}
          />
          {modeles.map((m) => (
            <FilterChip
              key={m.id}
              label={m.nom}
              selected={filterModele === m.nom}
              onPress={() => setFilterModele(filterModele === m.nom ? '' : m.nom)}
            />
          ))}
        </ScrollView>
      ) : (
        <View style={styles.simActions}>
          <FilterChip
            label={simSelectionMode ? 'Annuler la sélection' : 'Recharger plusieurs cartes'}
            icon={simSelectionMode ? 'close' : 'checkbox-multiple-marked-outline'}
            selected={simSelectionMode}
            onPress={toggleSimSelectionMode}
          />
          <Button
            mode="outlined"
            compact
            icon="download"
            textColor={colors.textPrimary}
            disabled={numerosRechargeExpiree.length === 0}
            onPress={() => void handleDownloadNumerosExpirees()}
          >
            Numéros +2 mois ({numerosRechargeExpiree.length})
          </Button>
          {activeRechargeJob && (
            <Button
              mode="outlined"
              compact
              icon="progress-clock"
              textColor={colors.textPrimary}
              onPress={() => setShowRechargeProgress(true)}
            >
              Suivi recharge
            </Button>
          )}
          {simSelectionMode && selectedSimIds.length > 0 && (
            <Button
              mode="contained"
              compact
              icon="cash-plus"
              loading={rechargingSim}
              disabled={rechargingSim}
              onPress={() => confirmRechargeSims(selectedSimIds)}
            >
              Recharger ({selectedSimIds.length})
            </Button>
          )}
        </View>
      )}

      <SectionHeader
        title={filtersActive ? 'Résultats' : isBoitierTab ? 'Tous les boîtiers' : 'Toutes les cartes SIM'}
        subtitle={loadingList
          ? 'Chargement…'
          : filtersActive
            ? `${plural(shownCount, 'résultat')} sur ${totalCount}`
            : `${plural(totalCount, 'article')} en stock`}
        right={!isBoitierTab && simSelectionMode ? (
          <Badge
            label={`${selectedSimIds.length} sélectionnée${selectedSimIds.length > 1 ? 's' : ''}`}
            tone="primary"
            icon="checkbox-multiple-marked-outline"
          />
        ) : undefined}
        style={styles.listHeading}
      />
    </View>
  );

  const listContentStyle = [
    styles.listContent,
    { paddingHorizontal: gutter, paddingTop: isDesktop ? 32 : 16, paddingBottom: isDesktop ? 48 : 160 },
  ];
  const loadingIndicator = (
    <View style={styles.loading}>
      <ActivityIndicator color={colors.primary} />
    </View>
  );

  return (
    <View style={styles.root}>
      {isBoitierTab ? (
        <FlatList
          style={styles.list}
          data={loadingList ? [] : filteredBoitiers}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderBoitierItem}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={loadingList ? loadingIndicator : (
            <EmptyState
              icon={TAB_ICONS.BOITIER}
              message={filterStatutBoitier || filterImeiDebounced || filterModele
                ? 'Aucun boîtier ne correspond aux filtres'
                : 'Aucun boîtier en stock'}
              hint={filterStatutBoitier || filterImeiDebounced || filterModele
                ? 'Modifiez la recherche, le modèle ou le statut sélectionné.'
                : 'Ajoutez des boîtiers GPS pour les retrouver ici.'}
            />
          )}
          contentContainerStyle={listContentStyle}
          keyboardShouldPersistTaps="handled"
        />
      ) : (
        <FlatList
          style={styles.list}
          data={loadingList ? [] : filteredSims}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderSimItem}
          ListHeaderComponent={listHeader}
          ListEmptyComponent={loadingList ? loadingIndicator : (
            <EmptyState
              icon={TAB_ICONS.SIM}
              message={filterStatutSim || filterNumeroDebounced
                ? 'Aucune carte SIM ne correspond aux filtres'
                : 'Aucune carte SIM en stock'}
              hint={filterStatutSim || filterNumeroDebounced
                ? 'Modifiez la recherche ou le statut sélectionné.'
                : 'Ajoutez des cartes SIM pour les retrouver ici.'}
            />
          )}
          contentContainerStyle={listContentStyle}
          keyboardShouldPersistTaps="handled"
        />
      )}

      {!isDesktop && (
        <>
          <FAB
            icon={isBoitierTab ? TAB_ICONS.SIM : TAB_ICONS.BOITIER}
            size="small"
            style={styles.fabSecondary}
            color={colors.textPrimary}
            accessibilityLabel={isBoitierTab ? 'Ajouter des cartes SIM' : 'Ajouter des boîtiers'}
            onPress={isBoitierTab ? openAddSimModal : openAddBoitierModal}
          />
          <FAB
            icon={isBoitierTab ? TAB_ICONS.BOITIER : TAB_ICONS.SIM}
            style={styles.fab}
            color={colors.onPrimary}
            accessibilityLabel={isBoitierTab ? 'Ajouter des boîtiers' : 'Ajouter des cartes SIM'}
            onPress={isBoitierTab ? openAddBoitierModal : openAddSimModal}
          />
        </>
      )}

      <Portal>
        <Modal
          visible={showAddBoitierModal}
          onDismiss={() => setShowAddBoitierModal(false)}
          contentContainerStyle={[styles.modal, { width: modalWidth }]}
        >
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle} accessibilityRole="header">Ajouter des boîtiers GPS</Text>
              <Text style={styles.modalSubtitle}>Choisissez le modèle puis saisissez un ou plusieurs IMEI.</Text>
            </View>

            <Text style={styles.fieldLabel}>Modèle *</Text>
            {modeles.length > 0 ? (
              <View style={styles.optionRow}>
                {modeles.map((m) => (
                  <FilterChip
                    key={m.id}
                    label={m.nom}
                    selected={addBoitierModele === m.nom}
                    onPress={() => setAddBoitierModele(m.nom)}
                  />
                ))}
              </View>
            ) : (
              <TextInput label="Modèle *" value={addBoitierModele} onChangeText={setAddBoitierModele} style={styles.input} />
            )}
            {!addBoitierModele.trim() && (
              <Text style={styles.validationHint}>Le modèle du boîtier est obligatoire.</Text>
            )}

            <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>IMEI des boîtiers</Text>
            <Text style={styles.fieldHint}>Exactement 15 chiffres par ligne.</Text>
            {addImeis.map((val, idx) => (
              <View key={idx} style={styles.refRow}>
                <TextInput
                  style={styles.refInput}
                  dense
                  value={val}
                  onChangeText={(t) => updateImeiLine(idx, t)}
                  keyboardType="numeric"
                  maxLength={15}
                  placeholder="000000000000000"
                  error={val.length > 0 && !isValidImei(val)}
                />
                <View style={styles.validSlot}>
                  {isValidImei(val) && (
                    <MaterialCommunityIcons name="check-circle" size={22} color={colors.success} accessibilityLabel="IMEI valide" />
                  )}
                </View>
                {addImeis.length > 1 && (
                  <IconCircleButton
                    icon="minus-circle-outline"
                    variant="ghost"
                    size={36}
                    color={colors.danger}
                    accessibilityLabel="Retirer cette ligne"
                    onPress={() => removeImeiLine(idx)}
                  />
                )}
              </View>
            ))}
            <Button icon="plus" mode="text" textColor={colors.textPrimary} onPress={addImeiLine} style={styles.addLineBtn} compact>
              Ajouter une ligne
            </Button>

            <View style={styles.modalActions}>
              <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowAddBoitierModal(false)}>Annuler</Button>
              <Button
                mode="contained"
                onPress={handleAddBoitiers}
                loading={savingBoitier}
                disabled={savingBoitier || addImeis.every((v) => !isValidImei(v)) || !addBoitierModele.trim()}
              >
                Enregistrer
              </Button>
            </View>
          </ScrollView>
        </Modal>

        <Modal
          visible={showAddSimModal}
          onDismiss={() => setShowAddSimModal(false)}
          contentContainerStyle={[styles.modal, { width: modalWidth }]}
        >
          <ScrollView showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle} accessibilityRole="header">Ajouter des cartes SIM</Text>
              <Text style={styles.modalSubtitle}>Saisissez un ou plusieurs numéros de téléphone.</Text>
            </View>

            <Text style={styles.fieldLabel}>N° de téléphone SIM</Text>
            <Text style={styles.fieldHint}>Exactement 10 chiffres par ligne.</Text>
            {addNumeros.map((val, idx) => (
              <View key={idx} style={styles.refRow}>
                <TextInput
                  style={styles.refInput}
                  dense
                  value={val}
                  onChangeText={(t) => updateSimLine(idx, t)}
                  keyboardType="phone-pad"
                  maxLength={10}
                  placeholder="0600000000"
                  error={val.length > 0 && !isValidSim(val)}
                />
                <View style={styles.validSlot}>
                  {isValidSim(val) && (
                    <MaterialCommunityIcons name="check-circle" size={22} color={colors.success} accessibilityLabel="Numéro valide" />
                  )}
                </View>
                {addNumeros.length > 1 && (
                  <IconCircleButton
                    icon="minus-circle-outline"
                    variant="ghost"
                    size={36}
                    color={colors.danger}
                    accessibilityLabel="Retirer cette ligne"
                    onPress={() => removeSimLine(idx)}
                  />
                )}
              </View>
            ))}
            <Button icon="plus" mode="text" textColor={colors.textPrimary} onPress={addSimLine} style={styles.addLineBtn} compact>
              Ajouter une ligne
            </Button>

            <View style={styles.modalActions}>
              <Button mode="text" textColor={colors.textPrimary} onPress={() => setShowAddSimModal(false)}>Annuler</Button>
              <Button
                mode="contained"
                onPress={handleAddSims}
                loading={savingSim}
                disabled={savingSim || addNumeros.every((v) => !isValidSim(v))}
              >
                Enregistrer
              </Button>
            </View>
          </ScrollView>
        </Modal>

        <Modal
          visible={showEditModal}
          onDismiss={() => setShowEditModal(false)}
          contentContainerStyle={[styles.modal, { width: modalWidth }]}
        >
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle} accessibilityRole="header">Modifier l’article</Text>
            {editingItem?.modele ? (
              <Text style={styles.modalSubtitle}>{editingItem.modele}</Text>
            ) : null}
          </View>
          <TextInput
            label={editingItem?.type === 'BOITIER' ? 'IMEI (15 chiffres)' : 'N° Tél SIM (10 chiffres)'}
            value={editRef}
            onChangeText={(t) =>
              setEditRef(t.replace(/\D/g, '').slice(0, editingItem?.type === 'BOITIER' ? 15 : 10))
            }
            keyboardType="numeric"
            maxLength={editingItem?.type === 'BOITIER' ? 15 : 10}
            style={styles.input}
            error={editRef.length > 0 && (editingItem?.type === 'BOITIER' ? !isValidImei(editRef) : !isValidSim(editRef))}
          />
          <Text style={[styles.fieldLabel, styles.fieldLabelSpaced]}>Nouveau statut</Text>
          <View style={styles.optionRow}>
            {(editingItem?.statut === 'AFFECTE'
              ? (['DISPONIBLE', 'AFFECTE', 'DEFECTUEUX'] as StatutStock[])
              : (['DISPONIBLE', 'DEFECTUEUX'] as StatutStock[])
            ).map((statut) => (
              <FilterChip
                key={statut}
                label={STATUT_LABELS[statut]}
                icon={STATUT_ICONS[statut]}
                selected={editStatut === statut}
                disabled={statut === 'AFFECTE'}
                onPress={() => setEditStatut(statut)}
              />
            ))}
          </View>
          {editingItem?.statut === 'AFFECTE' ? (
            <View style={styles.note}>
              <MaterialCommunityIcons name="information-outline" size={18} color={colors.info} />
              <Text style={styles.noteText}>
                Cet article est affecté à un véhicule. La dissociation se fait depuis le profil client.
              </Text>
            </View>
          ) : null}
          <View style={[styles.modalActions, styles.modalActionsSplit]}>
            {editingItem?.statut !== 'AFFECTE' ? (
              <Button
                mode="text"
                icon="delete-outline"
                onPress={handleDelete}
                textColor={colors.danger}
                loading={deleting}
                disabled={deleting || savingEdit}
              >
                Supprimer
              </Button>
            ) : (
              <Text style={styles.deleteHint}>Suppression disponible uniquement pour un élément non affecté.</Text>
            )}
            <Button
              mode="contained"
              onPress={handleUpdateStatut}
              loading={savingEdit}
              disabled={
                savingEdit || deleting ||
                (editingItem?.type === 'BOITIER' ? !isValidImei(editRef) : !isValidSim(editRef))
              }
            >Enregistrer</Button>
          </View>
        </Modal>
      </Portal>

      <SimRechargeProgressModal
        visible={showRechargeProgress}
        job={activeRechargeJob}
        creating={rechargeJobCreating}
        polling={rechargeJobPolling}
        pollingPaused={rechargePollingPaused}
        error={rechargeTrackingMessage ?? rechargeJobError}
        onDismiss={closeRechargeProgress}
        onResumePolling={resumeRechargeTracking}
        onStop={() => handleStopRecharge()}
        onRetryFailed={(simIds) => void handleRechargeSims(simIds)}
      />
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  root: { flex: 1, backgroundColor: c.background },
  list: { flex: 1 },
  listContent: { flexGrow: 1, width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center' },
  loading: { paddingVertical: 48, alignItems: 'center' },

  // Category tabs
  tabs: {
    flexDirection: 'row',
    gap: 32,
    borderBottomWidth: 1,
    borderBottomColor: c.divider,
    marginBottom: 24,
  },
  tab: { alignItems: 'center', gap: 6, paddingTop: 4, minWidth: 72 },
  tabLabel: { ...TYPOGRAPHY.label, color: c.textSecondary },
  tabLabelActive: { color: c.textPrimary },
  tabUnderline: {
    alignSelf: 'stretch',
    height: 2,
    marginTop: 8,
    marginBottom: -1,
    borderRadius: 1,
    backgroundColor: 'transparent',
  },
  tabUnderlineActive: { backgroundColor: c.ink },
  tabUnderlineHovered: { backgroundColor: c.border },

  // KPI tiles (desktop) / status chips (mobile)
  kpiRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 24 },
  kpiItem: { flex: 1, minWidth: 200 },
  kpiCard: { minWidth: 0 },
  kpiCardHovered: { borderColor: c.border },
  kpiCardSelected: { borderColor: c.ink, borderWidth: 2, padding: 19 },
  kpiCardPressed: { transform: [{ scale: 0.99 }] },
  chipScroll: { flexGrow: 0, marginBottom: 16 },
  chipRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 2 },

  // Search + toolbar
  search: { marginBottom: 16 },
  searchDesktop: { maxWidth: 560 },
  simActions: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 },
  listHeading: { marginTop: 8 },

  // Rows grouped in a single card, separated by hairlines
  row: {
    backgroundColor: c.surface,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: c.divider,
  },
  rowFirst: { borderTopWidth: 1, borderTopLeftRadius: RADIUS.lg, borderTopRightRadius: RADIUS.lg },
  rowLast: { borderBottomWidth: 1, borderBottomLeftRadius: RADIUS.lg, borderBottomRightRadius: RADIUS.lg },
  rowSelected: { backgroundColor: c.surfaceAlt },
  rowInner: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 16, marginHorizontal: 20 },
  rowInnerMobile: { gap: 12, marginHorizontal: 16 },
  rowDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  rowIcon: {
    width: 44,
    height: 44,
    borderRadius: RADIUS.md,
    backgroundColor: c.surfaceAlt,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowMain: { flex: 1, minWidth: 0 },
  itemRef: { ...TYPOGRAPHY.bodyStrong, color: c.textPrimary, fontVariant: ['tabular-nums' as const] },
  itemModele: { fontSize: 14, lineHeight: 20, color: c.textSecondary, marginTop: 2 },
  itemMeta: { ...TYPOGRAPHY.caption, color: c.textMuted, marginTop: 4 },
  itemBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  rowSide: { alignItems: 'flex-end', gap: 4 },
  rowSideDesktop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowActions: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 6,
    borderRadius: RADIUS.pill,
  },
  statusPillHovered: { backgroundColor: c.surfaceAlt },

  // Mobile FABs
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: c.primary },
  fabSecondary: {
    position: 'absolute',
    right: 24,
    bottom: 88,
    backgroundColor: c.surface,
    borderWidth: 1,
    borderColor: c.divider,
  },

  // Modals
  modal: {
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    marginVertical: 16,
    maxWidth: 560,
    alignSelf: 'center',
    maxHeight: '88%',
  },
  modalHeader: { marginBottom: 20 },
  modalTitle: { ...TYPOGRAPHY.title, color: c.textPrimary },
  modalSubtitle: { ...TYPOGRAPHY.body, color: c.textSecondary, marginTop: 4 },
  fieldLabel: { ...TYPOGRAPHY.label, color: c.textPrimary, marginBottom: 8 },
  fieldLabelSpaced: { marginTop: 8 },
  fieldHint: { ...TYPOGRAPHY.caption, color: c.textSecondary, marginTop: -4, marginBottom: 10 },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  input: { marginBottom: 12 },
  refRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  refInput: { flex: 1 },
  validSlot: { width: 24, alignItems: 'center' },
  validationHint: { ...TYPOGRAPHY.caption, color: c.danger, marginTop: -4, marginBottom: 12 },
  addLineBtn: { alignSelf: 'flex-start', marginTop: 4 },
  note: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.infoSoft,
    marginBottom: 4,
  },
  noteText: { flex: 1, fontSize: 14, lineHeight: 20, color: c.textPrimary },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: c.divider,
  },
  modalActionsSplit: { justifyContent: 'space-between' },
  deleteHint: { flex: 1, ...TYPOGRAPHY.caption, color: c.textSecondary },
}));
